import {
  type HTMLAttributes,
  KeyboardEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import {
  attachClosestEdge,
  extractClosestEdge
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/dist/types/types';
import invariant from 'tiny-invariant';

import { ConfirmationModal, Dropdown, Icon, Tooltip } from '@components/index';
import { STORAGE_KEYS } from '@constants/storage';
import {
  INPUT_MAX_LENGTH,
  INPUT_MIN_LENGTH
} from '@features/lead-management/constants/common';
import {
  useBulkUpdateStatusMutation,
  useLazyGetCountByStatusIdQuery
} from '@features/lead-management/queries/LeadManagementQuery';
import useNotify from '@hooks/notify';
import useIsTruncated from '@hooks/useIsTruncated';
import { ApiError } from '@types';
import { getLocalStorageItem } from '@utils/storage';

import {
  getStatusData,
  isStatusData,
  LeadStatusItem,
  StatusDragState
} from './status-data';

import theme from '../../../../../themes/colors';

const stateStyles: {
  [Key in StatusDragState['type']]?: HTMLAttributes<HTMLDivElement>['className'];
} = {
  'is-dragging': '!bg-grey20 opacity-50'
};

const idle: StatusDragState = { type: 'idle' };

interface DragPreviewProps {
  status: LeadStatusItem;
  isEditing: boolean;
}

const DragPreview = ({ status, isEditing }: DragPreviewProps) => (
  <div className="flex w-120 items-center justify-between rounded-lg border border-grey40 bg-white px-4 py-2 shadow-lg">
    <div className="flex min-w-0 items-center gap-3">
      <Icon name="reorder-handle" size="none" className="size-4 text-grey" />
      <div
        className={`w-full truncate px-4 py-2.5 text-sm font-medium text-content ${
          isEditing ? 'rounded-lg border border-primary' : ''
        }`}
      >
        {status.value}
      </div>
    </div>
  </div>
);

interface DropIndicatorProps {
  edge: Edge;
}

const StatusDropIndicator = ({ edge }: DropIndicatorProps) => (
  <div
    className={`pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-primary ${
      edge === 'top' ? '-top-1' : '-bottom-1'
    }`}
  />
);

interface DraggableStatusItemProps {
  status: LeadStatusItem;
  allStatuses: LeadStatusItem[];
  onSave: (status: LeadStatusItem, newValue: string) => Promise<boolean>;
  onDelete: (status: LeadStatusItem, replacementStatusId: string) => void;
  isEditing?: boolean;
  onEditStart?: () => void;
  onEditEnd?: () => void;
}

const DraggableStatusItem = ({
  status,
  allStatuses,
  onSave,
  onDelete,
  isEditing = false,
  onEditStart,
  onEditEnd
}: DraggableStatusItemProps) => {
  const { createErrorAlert, createSuccessAlert } = useNotify();

  const ref = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { ref: nameRef, isTruncated } = useIsTruncated<HTMLDivElement>(
    status.value
  );
  const [state, setState] = useState<StatusDragState>(idle);
  const [editValue, setEditValue] = useState(status.value);
  const [isLocalEditing, setIsLocalEditing] = useState(isEditing);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [replacementStatusId, setReplacementStatusId] = useState<string>('');
  const [leadCountDetails, setLeadCountDetails] = useState<{
    lead_ids: string[];
  } | null>(null);

  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  // Get available statuses for replacement (exclude current status)
  const availableStatuses = allStatuses
    .filter((s) => s.id !== status.id)
    .map((s) => ({
      id: s.id,
      label: s.value,
      value: s.id,
      element: <span className="min-w-0 flex-1 break-words">{s.value}</span>
    }));

  const [getCountByStatusId] = useLazyGetCountByStatusIdQuery();
  const [bulkUpdateStatus] = useBulkUpdateStatusMutation();

  useEffect(() => {
    setIsLocalEditing(isEditing);
    if (isEditing) {
      setEditValue(status.value);
    }
  }, [isEditing, status.value]);

  useEffect(() => {
    if (isLocalEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLocalEditing]);

  useEffect(() => {
    const element = ref.current;
    invariant(element);

    if (status.isLocked || isLocalEditing) {
      return undefined;
    }

    return combine(
      draggable({
        element,
        getInitialData() {
          return getStatusData(status);
        },
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({
              x: '16px',
              y: '8px'
            }),
            render({ container }) {
              setState({ type: 'preview', container });
            }
          });
        },
        onDragStart() {
          setState({ type: 'is-dragging' });
          document.body.style.cursor = 'grabbing';
        },
        onDrop() {
          setState(idle);
          document.body.style.cursor = '';
        }
      }),
      dropTargetForElements({
        element,
        canDrop({ source }) {
          if (source.element === element) {
            return false;
          }
          return isStatusData(source.data);
        },
        getData({ input }) {
          const data = getStatusData(status);
          return attachClosestEdge(data, {
            element,
            input,
            allowedEdges: ['top', 'bottom']
          });
        },
        getIsSticky() {
          return true;
        },
        onDragEnter({ self }) {
          const closestEdge = extractClosestEdge(self.data);
          setState({ type: 'is-dragging-over', closestEdge });
        },
        onDrag({ self }) {
          const closestEdge = extractClosestEdge(self.data);
          setState((current) => {
            if (
              current.type === 'is-dragging-over' &&
              current.closestEdge === closestEdge
            ) {
              return current;
            }
            return { type: 'is-dragging-over', closestEdge };
          });
        },
        onDragLeave() {
          setState(idle);
        },
        onDrop() {
          setState(idle);
        }
      })
    );
  }, [status, isLocalEditing]);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLocalEditing(true);
    onEditStart?.();
  };

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!organizationId) return;
    const { data: countData } = await getCountByStatusId({
      organizationId,
      statusId: status.id
    });
    if (countData) {
      setLeadCountDetails(countData);
    }
    setIsDeleteModalOpen(true);
  };

  const handleDeleteClickConfirm = async () => {
    const leadIds = leadCountDetails?.lead_ids || [];
    const hasLeads = leadIds.length > 0;

    // Only call bulk update API if there are actual leads to move
    if (hasLeads && replacementStatusId && leadIds.length > 0) {
      const payload = {
        organizationId,
        body: {
          lead_ids: leadIds,
          status_id: replacementStatusId
        }
      };
      const res = await bulkUpdateStatus(payload);
      if ('data' in res) {
        setLeadCountDetails(null);
      } else if ('error' in res) {
        const error = res.error as ApiError;
        createErrorAlert(error?.data?.message || 'Error while updating status');
        return; // Don't delete if bulk update failed
      }
    }

    // Delete the status (with or without replacement)
    onDelete(status, replacementStatusId || '');
    setIsDeleteModalOpen(false);
    setReplacementStatusId('');
  };

  const hasLeadsInStatus = (leadCountDetails?.lead_ids?.length ?? 0) > 0;

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setReplacementStatusId('');
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();
    const hasChanged = trimmedValue && trimmedValue !== status.value;

    if (hasChanged) {
      const success = await onSave(status, trimmedValue);
      if (success) {
        createSuccessAlert('Lead status updated successfully');
      } else {
        setEditValue(status.value);
      }
    }

    setIsLocalEditing(false);
    onEditEnd?.();
  };

  const handleCancel = () => {
    setEditValue(status.value);
    setIsLocalEditing(false);
    onEditEnd?.();
  };

  const handleBlur = () => {
    // On blur, cancel edit without saving (restore original value)
    handleCancel();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const deleteConfirmationMessage = hasLeadsInStatus
    ? `There are ${
        leadCountDetails?.lead_ids?.length || 0
      } leads currently in the "${
        status.value
      }" status. Select another status to move them before deletion.`
    : `Are you sure you want to delete the "${status.value}" status?`;

  return (
    <div className="relative">
      <div
        ref={ref}
        data-status-id={status.id}
        className={`group flex max-w-139 items-center justify-between rounded-lg border border-grey40 bg-white px-4 py-2 ${
          status.isLocked || isLocalEditing ? '' : 'cursor-grab hover:bg-grey10'
        } ${stateStyles[state.type] ?? ''}`}
      >
        <div className="mr-2 flex min-w-0 flex-1 items-center gap-3">
          {status.isLocked ? (
            <Icon
              name="lock-icon"
              size="none"
              className="size-4 text-disabledContent"
            />
          ) : (
            <Icon
              name="reorder-handle"
              size="none"
              className={`size-4 text-disabledContent ${
                isLocalEditing ? 'opacity-50' : ''
              }`}
            />
          )}
          {isLocalEditing ? (
            <input
              ref={inputRef}
              maxLength={INPUT_MAX_LENGTH}
              minLength={INPUT_MIN_LENGTH}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onClick={handleInputClick}
              className={`flex-1 text-base font-medium text-content outline-none ${
                isLocalEditing
                  ? 'rounded-lg !border !border-primary px-4 py-2.5'
                  : ''
              } placeholder:text-grey40`}
            />
          ) : (
            <>
              <div
                ref={nameRef}
                data-tooltip-id={`status-name-${status.id}`}
                className="w-full truncate px-4 py-2.5 text-base font-medium text-content"
              >
                {status.value}
              </div>
              {isTruncated && (
                <Tooltip
                  id={`status-name-${status.id}`}
                  className="!bg-cofeeNeutral10"
                >
                  <div className="max-w-xs whitespace-normal break-words text-cofeeNeutral1">
                    {status.value}
                  </div>
                </Tooltip>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isLocalEditing ? (
            <>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                className="rounded p-1 transition-opacity hover:bg-grey20"
                aria-label="Save"
              >
                <Icon
                  name="check-icon-green"
                  size="none"
                  className="size-3 text-primary"
                  stroke={theme.primary}
                />
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                className="rounded p-1 transition-opacity hover:bg-grey20"
                aria-label="Cancel"
              >
                <Icon
                  name="close-icon-red"
                  size="none"
                  className="size-2.5"
                  stroke={theme.error}
                />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleEditClick}
                className="rounded p-1 transition-opacity hover:bg-grey20 "
                aria-label={`Edit ${status.value}`}
              >
                <Icon
                  name="edit"
                  size="none"
                  className="size-4 text-grey"
                  stroke="theme.grey"
                />
              </button>
              {!status.isLocked && (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="rounded p-1 transition-opacity hover:bg-grey20"
                  aria-label={`Delete ${status.value}`}
                >
                  <Icon
                    name="trash"
                    size="none"
                    className="size-4 text-grey"
                    stroke="theme.grey"
                  />
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {state.type === 'is-dragging-over' && state.closestEdge && (
        <StatusDropIndicator edge={state.closestEdge as 'top' | 'bottom'} />
      )}
      {state.type === 'preview' &&
        createPortal(
          <DragPreview status={status} isEditing={isLocalEditing} />,
          state.container
        )}
      <ConfirmationModal
        iconName="trash-round"
        customModalIconStyle="!size-12 w-12 h-12 min-w-12 min-h-12"
        isOpen={isDeleteModalOpen}
        handleCancel={handleDeleteModalClose}
        handleConfirm={handleDeleteClickConfirm}
        title="Delete lead status?"
        message={
          <span className="block w-full min-w-0 whitespace-pre-wrap break-words">
            {deleteConfirmationMessage}
          </span>
        }
        messageStyle="!text-sm !text-cofeeNeutral8 !mb-5"
        confirmButtonLabel="Delete status"
        cancelButtonLabel="Cancel"
        confirmButtonStyle="text-white"
        cancelButtonStyle="text-content"
        confirmButtonLabelStyle="!text-white"
        primaryButtonVariant="primary"
        secondaryButtonVariant="tertiary"
        primaryButtonSize="medium"
        secondaryButtonSize="medium"
        className="w-[400px] !pb-6"
        isClickOutsideRequired={false}
        isConfirmButtonDisabled={!!(hasLeadsInStatus && !replacementStatusId)}
      >
        {hasLeadsInStatus ? (
          <div className="mb-5 flex items-center justify-center gap-2">
            <div className="text-sm font-medium text-content">
              Move leads to:
            </div>
            <Dropdown
              filterOptions={availableStatuses}
              currentFilter={replacementStatusId}
              onSelectFilter={(value: string) => setReplacementStatusId(value)}
              placeholder="Select a status"
              className="w-full max-w-[175px]"
              labelStyle="min-w-0 truncate"
              usePortal
              stopEventPropagation
            />
          </div>
        ) : null}
      </ConfirmationModal>
    </div>
  );
};

export default DraggableStatusItem;
